import { Div, InfoCard } from '@dnb/eufemia';
import { ApiWithStatusAndScopesDto } from '@portals/shared/portal/ApiDto';
import { groupBy } from '@portals/shared-frontend/utils';
import { useMemo } from 'react';

import { useDebounce } from '@/hooks/useDebounce';
import { ApiClientResponse, AppWithApiKeyAccessResponse, AppWithApiOAuthAccessResponse } from '@/openapi/schema';
import AppStatusDescription from '@/pages/profile/components/AppStatusDescription';

import AppTile from '../AppTile';
import { ViewType } from '../schemas';

export interface AppViewProps {
  consumerApps: Array<AppWithApiKeyAccessResponse | AppWithApiOAuthAccessResponse> | undefined;
  isLoading: boolean;
  clients: ApiClientResponse[] | undefined;
  query: string;
  showView: (view: ViewType, query?: string) => void;
  api: ApiWithStatusAndScopesDto;
}

export default function AppView({ consumerApps, isLoading, clients, query, showView, api }: AppViewProps) {
  const debouncedQuery = useDebounce(query, 300);

  const clientsGroupedByAppId = groupBy(clients || [], 'appId');

  const filteredConsumerApps = useMemo(() => {
    if (!consumerApps) return [];
    const lowerCaseQuery = debouncedQuery.toLowerCase();
    return consumerApps
      .filter((app) => {
        const appName = app.name.toLowerCase();
        const appId = app.id.toLowerCase();
        const appDescription = app.description?.toLowerCase();
        const serviceNowIdentifier = app.serviceNowIdentifier?.toLowerCase();
        const ownerName = app.owner?.name?.toLowerCase();
        const ownerEmail = app.owner?.email?.toLowerCase();
        const ownerId = app.owner?.id;
        const scopes = 'scopes' in app ? app.scopes.map((scope) => scope.name.toLowerCase()) : [];
        const env =
          'scopes' in app
            ? app.scopes.map((scope) => scope.environment.toLowerCase())
            : app.api.map((api) => api.environment.toLowerCase());

        const appClients = clientsGroupedByAppId[app.id] || [];
        const clientNames = appClients.map((client) => client.name?.toLowerCase() || '');
        const clientIds = appClients.map((client) => client.ciamClientId.toLowerCase());

        return (
          appName.includes(lowerCaseQuery) ||
          appId.includes(lowerCaseQuery) ||
          appDescription?.includes(lowerCaseQuery) ||
          serviceNowIdentifier?.includes(lowerCaseQuery) ||
          ownerName?.includes(lowerCaseQuery) ||
          ownerEmail?.includes(lowerCaseQuery) ||
          ownerId?.toLowerCase().includes(lowerCaseQuery) ||
          scopes.some((scope) => scope.includes(lowerCaseQuery)) ||
          env.some((environment) => environment.includes(lowerCaseQuery)) ||
          clientNames.some((clientName) => clientName.includes(lowerCaseQuery)) ||
          clientIds.some((clientId) => clientId.includes(lowerCaseQuery))
        );
      })
      .map((app) => {
        const appClients = clientsGroupedByAppId[app.id] || [];
        return (
          <Div bottom="small" key={app.id}>
            <AppTile api={api} app={app} clients={appClients} isLoading={isLoading} showView={showView} />
          </Div>
        );
      });
  }, [consumerApps, debouncedQuery, clientsGroupedByAppId, api, isLoading, showView]);

  if (consumerApps && !isLoading && consumerApps.length === 0) {
    return <InfoCard stretch text="Currently no apps have been granted access to this integration service." />;
  }

  return (
    <>
      <Div bottom="medium" top="medium">
        <AppStatusDescription />
      </Div>
      {filteredConsumerApps.length > 0 ? (
        filteredConsumerApps
      ) : (
        <InfoCard stretch text="No apps match the current filters." />
      )}
    </>
  );
}
